<template>
  <div class="ProductPage">
    <Breadcrumbs>
      <router-link
        :to="{ name: 'merchandise' }"
        v-bind:class="{ 'router-link-active': isProduct, active: isProduct }"
        >{{ getNavName(navName) }}</router-link
      >
      <span v-if="product">{{ product.name }}</span>
    </Breadcrumbs>
    <div class="contents pageBlock" v-if="product">
      <div class="goBack">
        <button class="backButton" type="button" @click="this.$router.go(-1)">
          {{ $t('NAV_BACK') }}
        </button>
      </div>
      <div class="description">
        <h1>{{ product.name }}</h1>
        <div class="content" v-html="product.description_long"></div>
        <form>
          <Alert :condition="parseInt(product.stock) <= 0">
            {{ $t('MERCHANDISE_OUT_OF_STOCK') }}
          </Alert>
          <div class="quantity">
            <NumberInputWithButtons
              v-model="quantity"
              :disabled="parseInt(product.stock, 10) === 0"
              label="MERCHANDISE_QUANTITY"
              min="0"
              :max="parseInt(product.stock)"
            ></NumberInputWithButtons>
          </div>
          <div v-if="isAnnualPass" class="annualPass">
            <fieldset v-for="i in quantity" :key="i">
              <legend>{{ $t('ANNUAL_TICKETS_CARD') }} {{ i }}</legend>
              <div class="setDetailsActions" v-if="isLoggedIn">
                <!--button class="asLink" v-on:click.prevent="setDataFromBillingInfo(i-1)">use billing information</button>
              <button class="asLink" v-if="shippingNotSameAsBilling" v-on:click.prevent="setDataFromShippingInfo(i-1)">use shipping information</button-->
              </div>
              <TextInput
                v-if="typeof names[i - 1].value !== 'undefined'"
                :label="$t('ANNUAL_TICKETS_NAME')"
                :model-value="names[i - 1].value"
                v-on:input="
                  (value) => {
                    names = { index: i - 1, value: value };
                  }
                "
              />

              <div class="checkboxInput" v-if="!isSNM">
                <input
                  type="checkbox"
                  :id="'hasCustomAddress-' + (i - 1)"
                  :checked="hasCustomAddresses[i - 1].value"
                  @input="(event) => (hasCustomAddresses = { index: i - 1, event: event })"
                />
                <label :for="'hasCustomAddress-' + (i - 1)">{{
                  $t('ANNUAL_TICKETS_SENT_TO_CUSTOM_ADDRESS')
                }}</label>
              </div>
              <TextAreaInput
                class="productAddress"
                v-if="hasCustomAddresses[i - 1].value || isSNM"
                :label="$t('ANNUAL_TICKETS_ADDRESS')"
                :fname="'address-' + i - 1"
                :model-value="addresses[i - 1].value"
                v-on:update:modelValue="
                  (value) => {
                    addresses = { index: i - 1, value: value };
                  }
                "
              />
              <div class="checkboxInput" v-if="isSNM">
                <input
                  type="checkbox"
                  :id="'sendToCustomAddress-' + (i - 1)"
                  :checked="sendToCustomAddresses[i - 1].value"
                  @input="(event) => (sendToCustomAddresses = { index: i - 1, event: event })"
                />
                <label :for="'sendToCustomAddress-' + (i - 1)">{{
                  $t('ANNUAL_TICKETS_SENT_TO_CUSTOM_ADDRESS')
                }}</label>
              </div>
            </fieldset>
          </div>
          <div class="price">
            <span class="label">{{ $t('MERCHANDISE_UNIT_PRICE') }}: </span>
            <span class="value">{{ formatPrice(product.price) }}</span>
          </div>
          <div class="priceTotal">
            <div class="label">
              {{ $t('MERCHANDISE_TOTAL') }}
              <template v-if="$te('ALL_PRICES_INCLUDE_VAT_PLUS_SHIPPING_PART1')">
                ({{ $t('ALL_PRICES_INCLUDE_VAT_PLUS_SHIPPING_PART1') }}
                <router-link :to="{ name: 'shippingInfo' }" target="_blank">{{
                  $t('ALL_PRICES_INCLUDE_VAT_PLUS_SHIPPING_PART2')
                }}</router-link
                >)
              </template>
              <template v-else> ({{ $t('ALL_PRICES_INCLUDE_VAT') }}) </template>
            </div>
            <div class="value">{{ formatPrice(totalPrice) }}</div>
          </div>
          <div class="actions">
            <button
              class="buyBtn mainAction"
              v-bind:class="{ hasItems: quantity > 0 }"
              @click.stop.prevent="add()"
              :disabled="quantity === 0 || parseInt(product.stock, 10) <= 0 || !isValidAnnualPass"
            >
              {{ $t('MERCHANDISE_BUY') }}
              <template
                v-if="
                  $t('MERCHANDISE_BUY_SUFFIX_SINGULAR') !== 'MERCHANDISE_BUY_SUFFIX_SINGULAR' &&
                  $t('MERCHANDISE_BUY_SUFFIX_PLURAL') !== 'MERCHANDISE_BUY_SUFFIX_PLURAL'
                "
              >
                <span class="quantity">{{ quantity }}</span>
                <span class="suffix">
                  <template v-if="quantity === 1">
                    {{ $t('MERCHANDISE_BUY_SUFFIX_SINGULAR') }}
                  </template>
                  <template v-else>
                    {{ $t('MERCHANDISE_BUY_SUFFIX_PLURAL') }}
                  </template>
                </span>
              </template>
            </button>
          </div>
        </form>
      </div>
      <div class="images">
        <Carousel class="carousel" :images="product.images"></Carousel>
      </div>

      <teleport to="head">
        <component :is="'script'" type="application/ld+json">
          {{ jsonld }}
        </component>
      </teleport>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Breadcrumbs from '../Breadcrumbs.vue';
import Carousel from '../Carousel.vue';
import NumberInputWithButtons from '../Form/NumberInputWithButtons.vue';
import TextInput from '../Form/TextInput.vue';
import TextAreaInput from '../Form/TextAreaInput.vue';
import formater from '../../lib/formater';
import strip from '../../lib/stripHtml';
import { setPageTitle } from '../../lib/pageTitle';
import Alert from '../Alert.vue';
import { useSeoMeta } from '@unhead/vue';

export default {
  name: 'ProductComponent',
  data() {
    return {
      navName: '',
    };
  },
  components: {
    Alert,
    Breadcrumbs,
    Carousel,
    NumberInputWithButtons,
    TextInput,
    TextAreaInput,
  },

  computed: {
    ...mapState({
      language: (state) => state.language,
      products: (state) => state.products,
      navTranslations: (state) => state.app.navTranslations,
      publicURL: (state) => state.app.API_PUBLIC_URL,
      currency: (state) => state.app.currencySymbol,
      isLoggedIn: (state) => state.user.isLoggedIn,
    }),
    isSNM() {
      if (['snm', 'devsnm'].includes(import.meta.env.VITE_SITE_FRONT_ID)) {
        return true;
      }
      return false;
    },
    jsonld() {
      if (typeof this.product === 'undefined') {
        return {};
      }
      const images = [];
      this.product.images.forEach((img) => {
        images.push(`${this.publicURL}/600/${img}`);
      });
      let stock = 'InStock';
      if (parseInt(this.product.stock, 10) <= 0) {
        stock = 'OutOfStock';
      }
      return {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: this.product.name,
        image: images,
        description: strip(this.product.description_long),
        offers: {
          '@type': 'Offer',
          url: window.location,
          priceCurrency: this.currency,
          price: (parseInt(this.product.price, 10) / 100).toFixed(2),
          itemCondition: 'https://schema.org/NewCondition',
          availability: `https://schema.org/${stock}`,
        },
      };
    },
    names: {
      get() {
        return this.$store.state.products.form.names;
      },
      set({ index, value }) {
        this.$store.dispatch('products/setName', { index, value });
      },
    },
    hasCustomAddresses: {
      get() {
        return this.$store.state.products.form.hasCustomAddresses;
      },
      set({ index }) {
        const v = !this.$store.state.products.form.hasCustomAddresses[index].value;
        this.$store.dispatch('products/setHasCustomAddress', { index, value: v });
      },
    },
    sendToCustomAddresses: {
      get() {
        return this.$store.state.products.form.sendToCustomAddresses;
      },
      set({ index }) {
        const v = !this.$store.state.products.form.sendToCustomAddresses[index].value;
        this.$store.dispatch('products/setSendToCustomAddress', { index, value: v });
      },
    },
    addresses: {
      get() {
        return this.$store.state.products.form.addresses;
      },
      set({ index, value }) {
        this.$store.dispatch('products/setAddress', { index, value });
      },
    },
    isProduct() {
      if (this.$route.name === 'product') {
        return true;
      }
      return false;
    },
    isValidAnnualPass() {
      if (!this.isAnnualPass) {
        return true;
      }
      if (
        this.names[0].value.trim() === '' ||
        ((this.hasCustomAddresses[0].value || this.isSNM) && this.addresses[0].value.trim() === '')
      ) {
        return false;
      }
      for (let i = 1; i < this.quantity; i++) {
        if (this.names[i].value.trim() === '') {
          return false;
        }
        if (this.hasCustomAddresses[i].value || this.isSNM) {
          if (this.addresses[i].value.trim() === '') {
            return false;
          }
        }
      }
      return true;
    },
    product() {
      const id = parseInt(this.$route.params.id, 10);
      let product;
      if (id) {
        product = this.$store.state.products.data.find((p) => parseInt(p.id, 10) === id);
      }
      return product;
    },
    shippingNotSameAsBilling() {
      return !this.$store.state.shipping.sameAsBilling;
    },
    isAnnualPass() {
      return this.product.product_kind_ref === '13';
    },
    quantity: {
      get() {
        return this.products.form.quantity;
      },
      set(val) {
        this.setQuantity(val);
      },
    },
    totalPrice() {
      return this.product.price * this.quantity;
    },
  },
  methods: {
    ...mapActions({
      getSingle: 'products/getSingle',
      resetForm: 'products/resetForm',
      addToCart: 'products/addToCart',
      setQuantity: 'products/setQuantity',
    }),
    formatPrice(value) {
      return formater.currency(value);
    },
    add() {
      const id = parseInt(this.$route.params.id, 10);
      this.addToCart({ id }).then(() => {
        this.$toasted.show(this.$t('MERCHANDISE_ADDED_TO_CART')).goAway(2000);
        this.$router.push({ name: 'checkout' });
      });
    },
    getNavName(type) {
      if (typeof this.navTranslations !== 'undefined') {
        if (typeof this.navTranslations[type] !== 'undefined') {
          if (
            typeof this.navTranslations[type][this.language] !== 'undefined' &&
            this.navTranslations[type][this.language] !== ''
          ) {
            return this.navTranslations[type][this.language];
          }
        }
      }
      return this.$t(type);
    },
    setDataFromBillingInfo(index) {
      const name = this.$store.state.customer.data.addrBilling.name;
      const address =
        this.$store.state.customer.data.addrBilling.street +
        '\n' +
        this.$store.state.customer.data.addrBilling.city +
        '\n' +
        this.$store.state.customer.data.addrBilling.zip +
        '\n' +
        this.$store.state.customer.data.addrBilling.country;
      this.names = { index, value: name };
      this.addresses = { index, value: address };
    },
    setDataFromShippingInfo(index) {
      const name = this.$store.state.customer.data.addrShipping.name;
      const address =
        this.$store.state.customer.data.addrShipping.street +
        '\n' +
        this.$store.state.customer.data.addrShipping.city +
        '\n' +
        this.$store.state.customer.data.addrShipping.zip +
        '\n' +
        this.$store.state.customer.data.addrShipping.country;
      this.names = { index, value: name };
      this.addresses = { index, value: address };
    },
  },
  mounted() {
    this.navName = this.getNavName('NAV_MERCHANDISE');
    if (this.$route.name === 'pass') {
      this.navName = this.getNavName('NAV_MEMCARD');
    }

    if (typeof this.$route.params.id !== 'undefined') {
      this.getSingle(this.$route.params.id).then(() => {
        if (typeof this.product === 'undefined') {
          this.$router.replace({ name: 'pageNotFound' });
        } else {
          this.resetForm();
          let imgURL = null;
          if (this.product.images.length > 0) {
            const baseURL = this.$store.state.app.API_PUBLIC_URL;
            imgURL = `${baseURL}/1600/${this.product.images[0]}`;
          }
          let description = strip(this.product.description_long);
          if (this.product.metadata) {
            description = strip(this.product.metadata);
          }

          useSeoMeta({
            title: setPageTitle(this.product.name),
            description,
            ogDescription: description,
            ogTitle: setPageTitle(this.product.name),
            ogImage: imgURL,
          });
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/grid';
@import '../../scss/mixins';
@import '../../scss/buttons';

.annualPass {
  margin-top: $sp;
  margin-bottom: 2 * $sp;
}

.goBack {
  padding-left: $sp;
}

.contents {
  @extend .row;
  @include blockPadding;
  background-color: $block-color;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}
.images,
.description {
  @extend .col-md-6;
  margin-top: $sp * 3;
}
h1 {
  margin-bottom: 2 * $sp;
}
.buyBtn {
  @extend .btn;
  @extend .btn-primary;
}
.quantity {
  display: inline-block;
  max-width: 15em;
}
.quantity::after {
  content: '';
  margin-right: 0.25em;
}

.priceTotal {
  display: flex;
  margin-top: 2 * $sp;
  justify-content: space-between;
  .label {
    font-weight: 600;
  }
  margin-bottom: 2 * $sp;
}

.total {
  display: none;
}

@media screen and (max-width: $screen-xs) {
  .total {
    display: inline;
  }
  button.mainAction {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 0;
    width: 100%;
    z-index: 20;
    height: 62px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
    opacity: 1;
    transition: all 0.5s;
  }
  button.mainAction:disabled {
    opacity: 1;
    background-color: darken($secondary-block-color-2, 30%) !important;
  }
}
fieldset {
  border: 1px solid #ddd;
  padding: $sp;
  legend {
    font-size: $font-size-base;
    border-bottom: 0;
    width: auto;
    padding-left: $sp;
    padding-right: $sp;
    margin-bottom: 0;
  }
}
button.asLink,
button.asLink:active {
  border: 0;
  background: transparent;
  text-decoration: underline;
  color: $link-color;
  box-shadow: none;
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
  outline: 0;
}
.setDetailsActions {
  text-align: right;
}
</style>
